/**
 * audio-saturation.js — гармоническое насыщение (window.AudioSaturation)
 *
 * ОДНА формула на все тулы. До 2026-08-06 она жила внутри mastering-chain.js,
 * и второму потребителю (/equalizer) пришлось бы либо тянуть всю цепочку
 * мастеринга, либо завести свою копию — а копия через месяц разошлась бы, и
 * «насыщение 40%» звучало бы в двух местах по-разному. Модуль крошечный
 * специально: у него нет зависимостей и его можно подключать откуда угодно.
 *
 * Кривая — гиперболический тангенс, нормированный так, чтобы ±1 оставался ±1.
 * Она СИММЕТРИЧНА, а значит даёт только НЕЧЁТНЫЕ гармоники (3-ю, 5-ю…) — это
 * не случайность: чётные окрашивают заметнее и на плотном материале читаются
 * как искажение. Замер на 40 Гц: 3-я гармоника (120 Гц) поднимается с −75.6 до
 * −21 дБ, чётные остаются в шуме.
 *
 * ⚠️ Кривую ОБЯЗАТЕЛЬНО ставить на WaveShaperNode с `oversample='4x'`. Без
 * передискретизации зеркала выше Найквиста заворачиваются обратно и слышны как
 * грязь (замерено в потоковом лейне: −36 дБ против −147 у оверсэмплённого).
 *
 * ⚠️⚠️ WaveShaper кладёт кривую на диапазон −1..+1 ЖЁСТКО: сигнал за пределами
 * зажимается краем кривой (reference_browser_api_silent_traps_2026_08_05).
 *
 * Топология — СЛОЖЕНИЕ, а не кроссфейд: полный сухой сигнал (усиление 1) плюс
 * насыщенная ветка с mix(). Так сделано в мастеринге, и повторять надо именно
 * это: на кроссфейде замер роста пиков дал противоположный ответ.
 */
(function () {
    'use strict';
    if (window.AudioSaturation) return;

    function clamp01(v) {
        if (typeof v !== 'number' || !isFinite(v)) return 0;
        return v > 1 ? 1 : (v < 0 ? 0 : v);
    }
    function drive(amount) { return 2 + 3 * clamp01(amount); }   // крутизна кривой
    /* Сколько подмешать — у каждой полосы своё (перекалибровано 2026-10-09).
       Прежнее 0.5·amount на обе полосы подбиралось, пока сухой путь шёл на
       192 отсчёта раньше мокрого: гребёнка гасила часть мокрого, и «сила»
       ручки была меньше, чем по формуле. Выровненная сумма когерентна — при
       том же 0.5 на 60 % верх 2–8 кГц вырос с +2.1 до +5.1 дБ, пик — с +1.85
       до +3.55 (порог стенда +2.5). Новые коэффициенты подобраны перебором на
       методике стенда equalizer-saturation.mjs (эталон — выход той же
       задержки): верх +2.2, середина −0.08, гармоники баса +36, пик +2.0 —
       прежняя сила эффекта и все пороги. Мастеринг (только верх) берёт 'high'. */
    var MIX_HIGH = 0.25, MIX_LOW = 0.3;
    function mix(amount, band) { return (band === 'low' ? MIX_LOW : MIX_HIGH) * clamp01(amount); }
    function curve(k, n) {
        n = n || 2048;
        var c = new Float32Array(n), t = Math.tanh(k);
        for (var i = 0; i < n; i++) {
            var x = (i * 2) / (n - 1) - 1;
            c[i] = Math.tanh(k * x) / t;
        }
        return c;
    }

    /* Какие полосы насыщать, а какую оставить в покое. Выбрано ЗАМЕРОМ пяти
       вариантов на одном материале (06.08):

         вариант        муть 200–500   верх 2–8к   бас 80–240   пик   алиасинг
         весь спектр       +4.87 ✗       +2.50      +48.8      +2.65   −62.3
         только верх       −0.18         +2.44        0        +0.45   −73.7
         только низ        −0.91         +0.12      +49.1      +1.23   −75.6
         низ + верх        −1.08 ✓       +2.34 ✓    +49.1 ✓    +1.75   −73.8

       Полнополосное мутит середину почти на 5 дБ — ровно то, на что говорят
       «стало хуже». Двухполосное берёт и верх («сочность»), и низ (гармоники,
       которые маленький динамик отдаёт вместо неслышимых 40 Гц), середину не
       трогает вовсе.
       ⚠️ В мастеринге насыщается ТОЛЬКО верх (SAT_SPLIT_HZ = 3000), и это не
       противоречие: там материал уже сведён и подъёма низа не делают. Здесь
       самый ходовой пресет — bass boost, поэтому низкая полоса и нужна. */
    var LOW_HZ = 150, HIGH_HZ = 3000, Q = 0.7071;

    /* Сухой путь — через ТАКОЙ ЖЕ WaveShaper ×4, что и мокрые ветки (2026-10-09).
       Передискретизация задерживает сигнал (Chromium 147 и 155: ×4 — 192 отсчёта,
       ×2 — 128, на любой частоте; в других движках — своё число). Сухой путь шёл
       напрямую, и сумма «сухой + мокрый на 4 мс позже» была гребёнкой: при
       насыщении 50 % отклик в 3–6 кГц — от −33.9 до +6.3 дБ с шагом 250 Гц, в
       басу −15…+5 дБ (замер тонами). Средняя энергия полосы этого не видит —
       поэтому таблица 06.08 его не показала.
       Тот же узел на сухом пути выравнивает пути сам, в любом браузере, без
       числа задержки: отклик сухого ×4 с линейной кривой ровен до 21 кГц
       (±0.003 дБ). Кривая жёстко зажимает всё за ±1 — поэтому ×1/8 до и ×8
       после: линейно до +18 dBFS (после подъёма полос сигнал бывает выше 0).
       Вся ступень теперь сдвигает звук целиком — офлайн-рендер, которому важна
       длина, вычитает это сам (latencyFrames). */
    var DRY_HEADROOM = 8, LINEAR = new Float32Array([-1, 1]);
    function alignedDry(ctx) {
        var pre = ctx.createGain(); pre.gain.value = 1 / DRY_HEADROOM;
        var ws = ctx.createWaveShaper(); ws.curve = LINEAR; ws.oversample = '4x';
        var post = ctx.createGain(); post.gain.value = DRY_HEADROOM;
        pre.connect(ws); ws.connect(post);
        return { input: pre, output: post };
    }

    /* Сколько отсчётов ступень с насыщением сдвигает звук на частоте sr —
       ЗАМЕРОМ в этом браузере (импульс через сухой путь), один раз на частоту. */
    var latencyCache = {};
    function latencyFrames(sr) {
        if (latencyCache[sr]) return latencyCache[sr];
        latencyCache[sr] = (async function () {
            try {
                var OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
                var n = 4096, ctx = new OAC(1, n, sr), b = ctx.createBuffer(1, n, sr);
                b.getChannelData(0)[0] = 0.5;
                var s = ctx.createBufferSource(); s.buffer = b;
                var d = alignedDry(ctx); s.connect(d.input); d.output.connect(ctx.destination); s.start(0);
                var y = (await ctx.startRendering()).getChannelData(0), k = 0;
                for (var i = 1; i < n; i++) if (Math.abs(y[i]) > Math.abs(y[k])) k = i;
                return k;
            } catch (_) { delete latencyCache[sr]; return 0; }
        })();
        return latencyCache[sr];
    }

    /* Собрать двухполосное насыщение и вернуть {input, output}. Никакой
       самодеятельности по месту: и живой предпросмотр, и офлайн-рендер строят
       его ЭТИМ вызовом, иначе экспорт однажды разойдётся с тем, что человек
       слышал в предпросмотре. */
    function buildDualBand(ctx, amount, opts) {
        var a = clamp01(amount);
        var input = ctx.createGain(), output = ctx.createGain();
        if (a <= 0) {
            // constantLatency: задержка ступени не зависит от ручки — нужно там, где
            // параллельные дорожки складываются (/audio-mixer): иначе дорожка с
            // насыщением уезжает от соседей на 192 отсчёта, а движение ручки
            // через 0 дёргает звук во времени.
            if (opts && opts.constantLatency) { var d0 = alignedDry(ctx); input.connect(d0.input); d0.output.connect(output); }
            else input.connect(output);
            return { input: input, output: output, active: false };
        }
        var lowHz = (opts && opts.lowHz) || LOW_HZ, highHz = (opts && opts.highHz) || HIGH_HZ;
        var dry = alignedDry(ctx);
        input.connect(dry.input); dry.output.connect(output);
        var shared = curve(drive(a));
        function branch(kind, hz) {
            var f = ctx.createBiquadFilter();
            f.type = kind; f.frequency.value = hz; f.Q.value = Q;
            var ws = ctx.createWaveShaper();
            ws.curve = shared; ws.oversample = '4x';
            var wet = ctx.createGain(); wet.gain.value = mix(a, kind === 'lowpass' ? 'low' : 'high');
            input.connect(f); f.connect(ws); ws.connect(wet); wet.connect(output);
        }
        branch('highpass', highHz);
        // low:false — только верх (так насыщает /music-mastering: материал уже
        // сведён, низ там не поднимают; см. SAT_SPLIT_HZ в mastering-chain.js).
        if (!(opts && opts.low === false)) branch('lowpass', lowHz);
        return { input: input, output: output, active: true };
    }

    window.AudioSaturation = {
        clamp01: clamp01, drive: drive, mix: mix, curve: curve,
        buildDualBand: buildDualBand, alignedDry: alignedDry, latencyFrames: latencyFrames,
        LOW_HZ: LOW_HZ, HIGH_HZ: HIGH_HZ
    };
})();
